<template>
  <WdsModal :show :title="$t('End jobs')" @cancel="$emit('cancel')">
    <WdsStack justify-center align-center direction-column gap-md class="content">
      <div class="icon-outer">
        <div class="icon-inner">
          <WdsIcon name="trash-can" color="#D12223" :size="24"></WdsIcon>
        </div>
      </div>

      <WdsText large>
        {{
          $tc(
            'Are you sure you want to end this job? | Are you sure you want to end :count jobs?',
            jobIds.length,
          )
        }}
      </WdsText>

      <WdsText v-if="err" large error>
        {{
          $t(
            'One or more jobs failed to be ended. Try again later or reach out to support for help.',
          )
        }}
      </WdsText>

      <WdsStack justify-space-between align-center class="tw-w-full">
        <WdsButton :disabled="loading" @click="$emit('cancel')">{{ $t('Cancel') }}</WdsButton>
        <WdsStack align-center gap-md>
          <WdsSpinner v-if="loading" indeterminate />
          <WdsButton :disabled="loading" error @click="submit">
            {{ $tc('End job | End jobs', jobIds.length) }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </WdsStack>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsIcon,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { ref } from 'vue'
import { DocumentNode } from '@apollo/client/link/core/types'
import { ApolloQueryResult } from '@apollo/client'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { EndJobMutation } from '@/components/Job/graphql/mutations'
import { graphql, useFragment } from '@/gql'
import { AllowedToEndJobQuery, JobActionColumnFragmentDoc } from '@/gql/graphql'

const emit = defineEmits(['cancel', 'submitted'])

const props = withDefaults(
  defineProps<{
    show: boolean
    jobIds: string[]
    refetchQueries?: Array<string | DocumentNode> | 'all' | 'active' | null
  }>(),
  {
    show: false,
    refetchQueries: null,
  },
)

const loading = ref(false)
const err = ref(false)
const submit = async () => {
  loading.value = true

  // So this is a very large bit of logic that works by checking the cache / querying for the viewerCanDelete value.
  // It uses this to filter already known jobs that will throw errors if we tried to delete them.
  // Without this every job where the viewer can't delete will throw an error.
  const ids = (
    await Promise.allSettled(
      props.jobIds.map((id) =>
        publicApi.query({
          query: graphql(`
            query AllowedToEndJob($id: ID!) {
              job(id: $id) {
                ...JobActionColumn
              }
            }
          `),
          variables: {
            id,
          },
          fetchPolicy: 'cache-first',
        }),
      ),
    )
  )
    .filter(({ status }) => status === 'fulfilled')
    .map((r) =>
      useFragment(
        JobActionColumnFragmentDoc,
        (r as PromiseFulfilledResult<ApolloQueryResult<AllowedToEndJobQuery>>).value.data.job,
      ),
    )
    .filter((job) => job?.viewerCanEdit)
    .map((job) => job?.id ?? '')

  const mutations = ids.map((id) =>
    publicApi.mutate({
      mutation: EndJobMutation,
      variables: {
        input: {
          id,
          accountId: currentAccount.globalId,
        },
      },
    }),
  )
  const res = await Promise.allSettled(mutations)
  loading.value = false

  err.value = res.some(({ status }) => status === 'rejected')
  if (props.refetchQueries != null) {
    await publicApi.refetchQueries({
      include: props.refetchQueries,
    })
  }

  if (!err.value) {
    emit('submitted')
  }
}
</script>

<style lang="scss" scoped>
.content {
  :deep(strong) {
    color: black;
  }

  .icon-outer {
    border-radius: 50%;
    background-color: #fdf2f2;
    padding: 6px;
    .icon-inner {
      padding: 5px;
      border-radius: 50%;
      background-color: #f9e0e0;
    }
  }
}
</style>
